import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { PublicLayout } from '@/components/layout/PublicLayout';
import { Card } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { PlatformAccess } from '@/components/PlatformAccess';
import { InputOTP, InputOTPGroup, InputOTPSlot } from '@/components/ui/input-otp';
import { api } from '@/lib/api';
import { useAuth } from '@/lib/auth';
import { toast } from 'sonner';
import { Eye, ShieldCheck, ArrowRight, Mail, MessageCircle, CheckCircle2 } from 'lucide-react';

const STEPS = [
    { key: 'account', label: 'Account', icon: Eye },
    { key: 'email', label: 'Email OTP', icon: Mail },
    { key: 'whatsapp', label: 'WhatsApp OTP', icon: MessageCircle },
];

function Stepper({ step }) {
    return (
        <div className="flex items-center justify-between mb-6">
            {STEPS.map((s, i) => {
                const idx = STEPS.findIndex(x => x.key === step);
                const Active = s.icon;
                const isDone = idx > i;
                const isCurrent = idx === i;
                return (
                    <React.Fragment key={s.key}>
                        <div className="flex items-center gap-2" data-testid={`register-step-${s.key}`}>
                            <div className={`h-8 w-8 rounded-full flex items-center justify-center text-[11px] font-semibold transition-colors ${
                                isCurrent ? 'bg-[#0E2A47] text-white' : isDone ? 'bg-[#1F9D8A] text-white' : 'bg-secondary text-muted-foreground'
                            }`}>
                                {isDone ? <CheckCircle2 className="h-4 w-4" /> : <Active className="h-4 w-4" />}
                            </div>
                            <span className={`text-xs font-medium ${isCurrent ? 'text-foreground' : 'text-muted-foreground'}`}>{s.label}</span>
                        </div>
                        {i < STEPS.length - 1 && (
                            <div className={`flex-1 h-px mx-2 ${isDone ? 'bg-[#1F9D8A]' : 'bg-border'}`} />
                        )}
                    </React.Fragment>
                );
            })}
        </div>
    );
}

export default function RegisterPage() {
    const [step, setStep] = useState('account');
    const [form, setForm] = useState({ password: '', confirm: '', mobile: '', name: '', email: '', platforms: [] });
    const [emailOtp, setEmailOtp] = useState('');
    const [waOtp, setWaOtp] = useState('');
    const [devEmailOtp, setDevEmailOtp] = useState('');
    const [devWaOtp, setDevWaOtp] = useState('');
    const [loading, setLoading] = useState(false);
    const [cooldown, setCooldown] = useState(0);
    const { loginUser } = useAuth();
    const navigate = useNavigate();

    const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

    useEffect(() => {
        if (cooldown <= 0) return undefined;
        const t = setInterval(() => setCooldown((c) => (c <= 1 ? 0 : c - 1)), 1000);
        return () => clearInterval(t);
    }, [cooldown]);
    const startCooldown = () => setCooldown(60);

    const sendEmailOtp = async () => {
        const { data } = await api.post('/auth/register/send-email-otp', { email: form.email.trim().toLowerCase() });
        if (data.dev_otp) { setDevEmailOtp(data.dev_otp); toast.info(`Dev email OTP: ${data.dev_otp}`); }
        else toast.success(data.message || 'OTP sent to your email.');
    };

    const sendWaOtp = async () => {
        const { data } = await api.post('/auth/register/send-whatsapp-otp', { mobile: form.mobile });
        if (data.dev_otp) { setDevWaOtp(data.dev_otp); toast.info(`Dev WhatsApp OTP: ${data.dev_otp}`); }
        else toast.success(data.message || 'OTP sent to your WhatsApp.');
    };

    const startRegistration = async () => {
        const email = form.email.trim().toLowerCase();
        if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) { toast.error('Enter a valid email address.'); return; }
        if (!form.name.trim()) { toast.error('Please enter your full (real) name.'); return; }
        if (!form.platforms.length) { toast.error('Select the platform(s) you want to recharge.'); return; }
        if (form.password.length < 6) { toast.error('Password must be at least 6 characters.'); return; }
        if (form.password !== form.confirm) { toast.error('Passwords do not match.'); return; }
        const mobile = form.mobile.replace(/\D/g, '').slice(-10);
        if (mobile.length !== 10) { toast.error('Enter a valid 10-digit Indian mobile number.'); return; }
        setLoading(true);
        try {
            await api.post('/auth/register/start', { email, password: form.password, mobile, name: form.name.trim(), platforms: form.platforms });
            set('mobile', mobile);
            await sendEmailOtp();
            startCooldown();
            setStep('email');
        } catch (err) {
            toast.error(err.response?.data?.detail || 'Could not start registration.', { duration: err.response?.status === 403 ? 12000 : 4000 });
        } finally { setLoading(false); }
    };

    const verifyEmailOtp = async () => {
        if (emailOtp.length !== 6) { toast.error('Enter the 6-digit email OTP.'); return; }
        setLoading(true);
        try {
            await api.post('/auth/register/verify-email-otp', { email: form.email.trim().toLowerCase(), code: emailOtp });
            toast.success('Email verified!');
            await sendWaOtp();
            startCooldown();
            setStep('whatsapp');
        } catch (err) {
            toast.error(err.response?.data?.detail || 'Invalid OTP.');
        } finally { setLoading(false); }
    };

    const verifyWaOtpAndComplete = async () => {
        if (waOtp.length !== 6) { toast.error('Enter the 6-digit WhatsApp OTP.'); return; }
        setLoading(true);
        try {
            await api.post('/auth/register/verify-whatsapp-otp', { mobile: form.mobile, code: waOtp });
            const fingerprint = `${navigator.platform}|${navigator.language}|${navigator.userAgent.slice(0, 80)}`;
            const { data } = await api.post('/auth/register/complete', {
                email: form.email.trim().toLowerCase(),
                device_fingerprint: fingerprint,
            });
            loginUser(data.token, data.user);
            toast.success('Account created! Welcome to Eagle Eyes.');
            navigate('/dashboard');
        } catch (err) {
            toast.error(err.response?.data?.detail || 'Could not complete registration.', { duration: err.response?.status === 403 ? 12000 : 4000 });
        } finally { setLoading(false); }
    };

    const resend = async (which) => {
        setLoading(true);
        try {
            if (which === 'email') await sendEmailOtp();
            else await sendWaOtp();
            startCooldown();
            toast.success('OTP resent.');
        } catch (err) {
            toast.error(err.response?.data?.detail || 'Failed to resend OTP.');
        } finally { setLoading(false); }
    };

    return (
        <PublicLayout>
            <div className="min-h-[70vh] flex items-center justify-center px-4 py-10">
                <Card className="max-w-lg w-full p-6 sm:p-8 shadow-card" data-testid="register-card">
                    <div className="flex items-center gap-2 mb-1">
                        <div className="h-10 w-10 rounded-lg bg-[#1F9D8A] text-white flex items-center justify-center">
                            <ShieldCheck className="h-5 w-5" />
                        </div>
                        <div>
                            <div className="font-display font-semibold text-lg">Create your account</div>
                            <div className="text-xs text-muted-foreground">Email OTP + WhatsApp OTP verification</div>
                        </div>
                    </div>

                    <div className="mt-6"><Stepper step={step} /></div>

                    {step === 'account' && (
                        <div className="space-y-4">
                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                                <div className="space-y-2">
                                    <Label htmlFor="email">Email</Label>
                                    <Input id="email" data-testid="register-email-input" type="email" placeholder="you@example.com"
                                        value={form.email} onChange={(e) => set('email', e.target.value)} autoComplete="email" />
                                </div>
                                <div className="space-y-2">
                                    <Label htmlFor="name">Full name (Real name)</Label>
                                    <Input id="name" data-testid="register-name-input" placeholder="Your real full name"
                                        value={form.name} onChange={(e) => set('name', e.target.value)} />
                                </div>
                            </div>
                            <div className="space-y-2">
                                <Label>Which platforms do you want to recharge?</Label>
                                <PlatformAccess value={form.platforms} onChange={(v) => set('platforms', v)} testid="register-platforms" />
                                <p className="text-[11px] text-muted-foreground">Only these will show in your account. Need another later? Ask support to enable it.</p>
                            </div>
                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                                <div className="space-y-2">
                                    <Label htmlFor="password">Password</Label>
                                    <Input id="password" data-testid="register-password-input" type="password" placeholder="At least 6 characters"
                                        value={form.password} onChange={(e) => set('password', e.target.value)} autoComplete="new-password" />
                                </div>
                                <div className="space-y-2">
                                    <Label htmlFor="confirm">Confirm password</Label>
                                    <Input id="confirm" data-testid="register-confirm-password-input" type="password" placeholder="Re-enter password"
                                        value={form.confirm} onChange={(e) => set('confirm', e.target.value)} autoComplete="new-password" />
                                </div>
                            </div>
                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                                <div className="space-y-2">
                                    <Label htmlFor="mobile">WhatsApp number (India)</Label>
                                    <Input id="mobile" data-testid="register-mobile-input" inputMode="numeric" placeholder="10-digit mobile"
                                        value={form.mobile} onChange={(e) => set('mobile', e.target.value.replace(/\D/g, '').slice(0, 12))} />
                                </div>
                            </div>
                            <Button disabled={loading} onClick={startRegistration}
                                className="w-full bg-[#0E2A47] hover:bg-[#0B223A]" data-testid="register-continue-button">
                                {loading ? 'Sending OTP…' : 'Continue'} <ArrowRight className="h-4 w-4 ml-2" />
                            </Button>
                        </div>
                    )}

                    {step === 'email' && (
                        <div className="space-y-4">
                            <div className="text-sm text-muted-foreground">
                                We sent a 6-digit code to your email <span className="font-medium text-foreground">{form.email}</span>
                            </div>
                            {devEmailOtp && (
                                <div className="text-xs bg-amber-50 border border-amber-200 text-amber-800 rounded-lg p-2">
                                    DEV mode OTP: <span className="font-mono font-semibold" data-testid="register-dev-email-otp">{devEmailOtp}</span>
                                </div>
                            )}
                            <div>
                                <Label className="block mb-2">Enter email OTP</Label>
                                <InputOTP maxLength={6} value={emailOtp} onChange={setEmailOtp} autoFocus data-testid="register-email-otp-input">
                                    <InputOTPGroup>{[...Array(6)].map((_, i) => <InputOTPSlot key={i} index={i} />)}</InputOTPGroup>
                                </InputOTP>
                            </div>
                            <div className="flex flex-col sm:flex-row gap-2">
                                <Button disabled={loading} onClick={verifyEmailOtp}
                                    className="bg-[#0E2A47] hover:bg-[#0B223A] w-full sm:flex-1" data-testid="register-verify-email-otp-button">
                                    {loading ? 'Verifying…' : 'Verify & continue'}
                                </Button>
                                <Button type="button" variant="outline" disabled={loading || cooldown > 0} onClick={() => resend('email')} data-testid="register-resend-email-otp-button">{cooldown > 0 ? `Resend in ${cooldown}s` : 'Resend'}</Button>
                            </div>
                            <button onClick={() => { setStep('account'); setEmailOtp(''); setDevEmailOtp(''); }}
                                className="text-xs text-muted-foreground hover:text-foreground underline" data-testid="register-back-account-button">
                                Edit account details
                            </button>
                        </div>
                    )}

                    {step === 'whatsapp' && (
                        <div className="space-y-4">
                            <div className="text-sm text-muted-foreground">
                                We sent a 6-digit code on WhatsApp to <span className="font-medium text-foreground">+91 {form.mobile}</span>
                            </div>
                            {devWaOtp && (
                                <div className="text-xs bg-amber-50 border border-amber-200 text-amber-800 rounded-lg p-2">
                                    DEV mode OTP: <span className="font-mono font-semibold" data-testid="register-dev-wa-otp">{devWaOtp}</span>
                                </div>
                            )}
                            <div>
                                <Label className="block mb-2">Enter WhatsApp OTP</Label>
                                <InputOTP maxLength={6} value={waOtp} onChange={setWaOtp} autoFocus data-testid="register-wa-otp-input">
                                    <InputOTPGroup>{[...Array(6)].map((_, i) => <InputOTPSlot key={i} index={i} />)}</InputOTPGroup>
                                </InputOTP>
                            </div>
                            <div className="flex flex-col sm:flex-row gap-2">
                                <Button disabled={loading} onClick={verifyWaOtpAndComplete}
                                    className="bg-[#0E2A47] hover:bg-[#0B223A] w-full sm:flex-1" data-testid="register-verify-wa-otp-button">
                                    {loading ? 'Creating account…' : 'Verify & create account'}
                                </Button>
                                <Button type="button" variant="outline" disabled={loading || cooldown > 0} onClick={() => resend('whatsapp')} data-testid="register-resend-wa-otp-button">{cooldown > 0 ? `Resend in ${cooldown}s` : 'Resend'}</Button>
                            </div>
                        </div>
                    )}

                    <div className="mt-6 pt-4 border-t border-border text-xs text-muted-foreground text-center">
                        Already have an account?{' '}
                        <Link to="/login" className="text-[#1F9D8A] font-medium" data-testid="register-login-link">Sign in</Link>
                    </div>
                </Card>
            </div>
        </PublicLayout>
    );
}
